<template>
  <div 
    class="simulator-container" 
    tabindex="0"
    @mousedown="handleContainerMouseDown"
  >
    <!-- Панель инструментов -->
    <div class="toolbar">
      <button @click="addComponent('battery')">🔋 Батарея</button>
      <button @click="addComponent('led')">💡 Светодиод</button>
      <button @click="addComponent('resistor')">📏 Резистор</button>
      <button @click="addComponent('switch')">🔘 Переключатель</button>
      <button @click="addComponent('capacitor')">⏀ Конденсатор</button>
      <button @click="addComponent('transistor_npn')">🔺 NPN</button>
      <button @click="addComponent('transistor_pnp')">🔻 PNP</button>
      <button @click="addComponent('node')">⚫ Узел</button>
      <button 
        @click="rotateSelected"
        :disabled="!selectedId || selectedId.startsWith('wire-')"
      >
        ↻ Повернуть
      </button>
      <button 
        @click="deleteSelected"
        :disabled="!selectedId"
      >
        🗑️ Удалить
      </button>
      <div class="toolbar-divider"></div>
      <button @click="loadScheme('blinking-led')">📋 Мигающий LED</button>
    </div>

    <!-- Панель свойств выбранного компонента -->
    <div v-if="selectedComp" class="properties-panel">
      <h3>{{ getLabel(selectedComp) }}</h3>
      
      <div class="prop-row" v-if="selectedComp.type === 'battery'">
        <label>Напряжение, В</label>
        <input 
          type="number" 
          step="0.1" 
          min="0.1" 
          max="24"
          :value="selectedComp.voltage"
          @input="e => updateParam(selectedComp, 'voltage', +e.target.value)"
        >
      </div>
      
      <div class="prop-row" v-if="selectedComp.type === 'resistor'">
        <label>Сопротивление, Ом</label>
        <input 
          type="number" 
          step="1" 
          min="1" 
          max="100000"
          :value="selectedComp.resistance"
          @input="e => updateParam(selectedComp, 'resistance', +e.target.value)"
        >
      </div>
      
      <div class="prop-row" v-if="selectedComp.type === 'led'">
        <label>Цвет свечения</label>
        <input 
          type="color" 
          :value="selectedComp.color"
          @input="e => updateParam(selectedComp, 'color', e.target.value)"
        >
      </div>
      
      <div class="prop-row" v-if="selectedComp.type === 'switch'">
        <label>Состояние</label>
        <button 
          class="toggle-btn"
          :class="{ on: selectedComp.isOn }"
          @click="toggleSwitch(selectedComp)"
        >
          {{ selectedComp.isOn ? 'Включен' : 'Выключен' }}
        </button>
      </div>
      
      <div class="prop-row" v-if="selectedComp.type === 'capacitor'">
        <label>Ёмкость, мкФ</label>
        <input 
          type="number" 
          step="10" 
          min="1" 
          max="100000"
          :value="selectedComp.capacitance"
          @input="e => updateParam(selectedComp, 'capacitance', +e.target.value)"
        >
      </div>
      
      <div class="prop-row read-only" v-if="selectedComp.type === 'capacitor'">
        <label>Заряд</label>
        <span class="charge-value">{{ Math.round(selectedComp.charge || 0) }}%</span>
      </div>
      
      <div class="prop-row" v-if="selectedComp.type === 'transistor_npn' || selectedComp.type === 'transistor_pnp'">
        <label>Коэффициент усиления (hFE)</label>
        <input 
          type="number" 
          step="10" 
          min="10" 
          max="1000"
          :value="selectedComp.gain"
          @input="e => updateParam(selectedComp, 'gain', +e.target.value)"
        >
      </div>
    </div>

    <!-- Рабочая область -->
    <div
      class="workspace"
      :class="{ panning: isPanning }"
      :style="{ transform: `translate(${panOffset.x}px, ${panOffset.y}px)` }"
      @click="clearSelection"
      ref="workspaceRef"
    >
      <!-- Компоненты -->
      <div
        v-for="comp in components"
        :key="comp.id"
        class="circuit-component"
        :class="[`type-${comp.type}`, { 
          selected: selectedId === comp.id, 
          powered: isPowered(comp), 
          dragging: isDragging && dragComp?.id === comp.id 
        }]"
        :style="getComponentStyle(comp)"
        @mousedown="startDrag(comp, $event)"
        @click.stop="selectComponent(comp)"
      >
        <!-- Точки соединения -->
        <div
          v-for="port in getPorts(comp)"
          :key="port.id"
          class="port"
          :class="[
            port.type, 
            { 
              connected: isPortConnected(comp.id, port.id),
              'connection-target': isValidConnectionTarget(comp, port)
            }
          ]"
          :style="getPortStyle(comp, port)"
          @mousedown="startConnection($event, comp, port)"
          @mouseup.stop="finishConnection($event, comp, port)"
          @mouseenter="hoveredPort = { componentId: comp.id, portId: port.id }"
          @mouseleave="hoveredPort = null"
        ></div>

        <!-- SVG символы компонентов -->
        <svg 
          v-if="comp.type === 'battery'"
          class="component-symbol" 
          viewBox="0 0 60 36" 
          width="60" 
          height="36"
        >
          <line x1="30" y1="0" x2="30" y2="8" stroke="#333" stroke-width="2"/>
          <line x1="30" y1="28" x2="30" y2="36" stroke="#333" stroke-width="2"/>
          <rect x="12" y="8" width="36" height="20" fill="#fff9c4" stroke="#333" stroke-width="2" rx="2"/>
          <line x1="20" y1="16" x2="40" y2="16" stroke="#333" stroke-width="2"/>
          <line x1="24" y1="20" x2="36" y2="20" stroke="#333" stroke-width="2"/>
        </svg>

        <svg 
          v-else-if="comp.type === 'resistor'"
          class="component-symbol" 
          viewBox="0 0 60 36" 
          width="60" 
          height="36"
        >
          <line x1="0" y1="18" x2="8" y2="18" stroke="#333" stroke-width="2"/>
          <polyline 
            points="8,18 12,8 16,28 20,8 24,28 28,8 32,28 36,18" 
            fill="none" 
            stroke="#333" 
            stroke-width="2"
            stroke-linejoin="round"
          />
          <line x1="36" y1="18" x2="60" y2="18" stroke="#333" stroke-width="2"/>
        </svg>

        <svg 
          v-else-if="comp.type === 'led'"
          class="component-symbol" 
          viewBox="0 0 60 36" 
          width="60" 
          height="36"
        >
          <line x1="0" y1="18" x2="14" y2="18" stroke="#333" stroke-width="2"/>
          <line x1="46" y1="18" x2="60" y2="18" stroke="#333" stroke-width="2"/>
          <polygon points="14,10 14,26 38,18" fill="none" stroke="#333" stroke-width="2"/>
          <line x1="38" y1="10" x2="38" y2="26" stroke="#333" stroke-width="2"/>
          <line x1="20" y1="4" x2="24" y2="0" stroke="#333" stroke-width="1.5"/>
          <line x1="26" y1="4" x2="30" y2="0" stroke="#333" stroke-width="1.5"/>
          <line x1="32" y1="4" x2="36" y2="0" stroke="#333" stroke-width="1.5"/>
        </svg>

        <svg 
          v-else-if="comp.type === 'switch'"
          class="component-symbol" 
          viewBox="0 0 60 36" 
          width="60" 
          height="36"
        >
          <circle cx="8" cy="18" r="3" fill="none" stroke="#333" stroke-width="2"/>
          <circle cx="52" cy="18" r="3" fill="none" stroke="#333" stroke-width="2"/>
          <line 
            x1="8" 
            y1="18" 
            x2="48" 
            y2="18" 
            stroke="#333" 
            stroke-width="2"
            stroke-linecap="round"
            :transform="comp.isOn ? '' : 'rotate(-25, 8, 18)'"
          />
        </svg>

        <svg 
          v-else-if="comp.type === 'capacitor'"
          class="component-symbol" 
          viewBox="0 0 60 36" 
          width="60" 
          height="36"
        >
          <line x1="0" y1="18" x2="22" y2="18" stroke="#333" stroke-width="2"/>
          <line x1="22" y1="8" x2="22" y2="28" stroke="#333" stroke-width="2"/>
          <line x1="38" y1="8" x2="38" y2="28" stroke="#333" stroke-width="2"/>
          <line x1="38" y1="18" x2="60" y2="18" stroke="#333" stroke-width="2"/>
          <!-- Индикатор заряда -->
          <rect 
            x="24" 
            y="12" 
            :width="Math.max(0, (comp.charge || 0) * 0.12)" 
            height="12" 
            fill="#4CAF50" 
            opacity="0.7"
            rx="2"
          />
        </svg>

        <svg 
          v-else-if="comp.type === 'transistor_npn'"
          class="component-symbol" 
          viewBox="0 0 60 36" 
          width="60" 
          height="36"
        >
          <line x1="30" y1="0" x2="30" y2="10" stroke="#333" stroke-width="2"/>
          <text x="34" y="9" font-size="8" fill="#555" font-weight="bold">C</text>
          <line x1="30" y1="18" x2="60" y2="18" stroke="#333" stroke-width="2"/>
          <text x="46" y="16" font-size="8" fill="#555" font-weight="bold">B</text>
          <line x1="30" y1="26" x2="30" y2="36" stroke="#333" stroke-width="2"/>
          <text x="34" y="33" font-size="8" fill="#555" font-weight="bold">E</text>
          <line x1="30" y1="10" x2="30" y2="26" stroke="#333" stroke-width="3"/>
          <polygon points="30,28 26,34 34,34" fill="#333"/>
        </svg>

        <svg 
          v-else-if="comp.type === 'transistor_pnp'"
          class="component-symbol" 
          viewBox="0 0 60 36" 
          width="60" 
          height="36"
        >
          <line x1="30" y1="0" x2="30" y2="10" stroke="#333" stroke-width="2"/>
          <text x="34" y="9" font-size="8" fill="#555" font-weight="bold">C</text>
          <line x1="30" y1="18" x2="60" y2="18" stroke="#333" stroke-width="2"/>
          <text x="46" y="16" font-size="8" fill="#555" font-weight="bold">B</text>
          <line x1="30" y1="26" x2="30" y2="36" stroke="#333" stroke-width="2"/>
          <text x="34" y="33" font-size="8" fill="#555" font-weight="bold">E</text>
          <line x1="30" y1="10" x2="30" y2="26" stroke="#333" stroke-width="3"/>
          <polygon points="30,28 26,22 34,22" fill="#333"/>
        </svg>

        <svg 
          v-else-if="comp.type === 'node'"
          class="component-symbol" 
          viewBox="0 0 16 16" 
          width="16" 
          height="16"
        >
          <circle cx="8" cy="8" r="4" fill="#333"/>
        </svg>

        <!-- Свечение LED -->
        <div 
          v-if="comp.type === 'led'"
          class="led-glow-overlay"
          :class="{ 'on': comp.isOn }"
          :style="{ '--led-color': comp.color || '#ff0000' }"
        ></div>

        <!-- Ручка переключателя -->
        <div 
          v-if="comp.type === 'switch'"
          class="switch-thumb-ui"
          :class="{ on: comp.isOn }"
          @mousedown.stop="toggleSwitch(comp)"
          title="Кликните, чтобы переключить"
        ></div>

        <!-- Подпись -->
        <div class="component-label">{{ getLabel(comp) }}</div>
      </div>

      <!-- Провода и узлы -->
      <svg class="wires" ref="wiresSvg">
        <path
          v-for="(wire, index) in wires"
          :key="'w-' + index"
          :d="getWirePath(wire)"
          :class="{ 
            'active': isCircuitClosed,
            'selected': selectedId === 'wire-' + index
          }"
          @click.stop="selectWire(wire, index)"
        />
        
        <circle
          v-for="(j, i) in junctions"
          :key="'j-' + i"
          :cx="j.x"
          :cy="j.y"
          r="4"
          fill="#333"
          class="junction"
        />
        
        <path
          v-if="tempWire"
          :d="getTempWirePath()"
          class="temp"
        />
      </svg>
    </div>

    <!-- Инфо-панель цепи -->
    <div class="circuit-info">
      <div class="circuit-status" :class="{ active: isCircuitClosed }">
        <span class="status-dot"></span>
        {{ isCircuitClosed ? 'Цепь замкнута' : 'Цепь разомкнута' }}
      </div>
      
      <div v-if="circuitInfo.isClosed" class="circuit-stats">
        <div class="stats-grid">
          <div class="stat-item">
            <span class="stat-label">Ток</span>
            <span class="stat-value">{{ circuitInfo.current.toFixed(3) }} А</span>
          </div>
          <div class="stat-item">
            <span class="stat-label">U батареи</span>
            <span class="stat-value">{{ circuitInfo.voltage.toFixed(1) }} В</span>
          </div>
          <div class="stat-item">
            <span class="stat-label">Мощность</span>
            <span class="stat-value">{{ circuitInfo.power.toFixed(3) }} Вт</span>
          </div>
          <div class="stat-item">
            <span class="stat-label">R общее</span>
            <span class="stat-value">{{ circuitInfo.totalResistance.toFixed(1) }} Ом</span>
          </div>
        </div>
      </div>
      
      <div class="components-list">
        <div class="list-header">Компоненты</div>
        <div 
          v-for="c in circuitInfo.components" 
          :key="c.id"
          class="comp-stat"
          :class="{ active: selectedId === c.id }"
          @click="selectedId = c.id"
        >
          <div class="comp-main">
            <span class="comp-name">{{ c.label }}</span>
            <span class="comp-params">
              <template v-if="c.type === 'battery'">{{ c.voltage }}В</template>
              <template v-if="c.type === 'resistor'">{{ c.resistance }}Ом</template>
              <template v-if="c.type === 'led'">
                <span class="color-dot" :style="{ background: c.color }"></span>
              </template>
              <template v-if="c.type === 'switch'">{{ c.isOn ? 'ON' : 'OFF' }}</template>
              <template v-if="c.type === 'capacitor'">
                {{ c.capacitance }}мкФ
                <span v-if="c.charge > 0" class="charge-badge">{{ Math.round(c.charge) }}%</span>
              </template>
              <template v-if="c.type === 'transistor_npn' || c.type === 'transistor_pnp'">{{ c.gain }}x</template>
            </span>
          </div>
          <div v-if="circuitInfo.isClosed" class="comp-calc">
            I={{ c.i.toFixed(3) }}А · U={{ c.u.toFixed(2) }}В · P={{ c.p.toFixed(3) }}Вт
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, watch, onMounted, onUnmounted, nextTick, computed } from 'vue'

// Состояние симулятора
const components = ref([])
const wires = ref([])
const tempWire = ref(null)
const isCircuitClosed = ref(false)
const selectedId = ref(null)
const hoveredPort = ref(null)
const activeComponentIds = ref([])

// Состояние мигания для готовых схем
let blinkTimer = null
const blinkMode = ref(false)
const blinkLeds = ref([])

// Состояние перетаскивания
const isDragging = ref(false)
const dragComp = ref(null)
const dragOffset = ref({ x: 0, y: 0 })
const workspaceRef = ref(null)

// Состояние панорамирования (зажатое колёсико)
const isPanning = ref(false)
const panOffset = ref({ x: 0, y: 0 })
const panStart = ref({ x: 0, y: 0 })
const panStartOffset = ref({ x: 0, y: 0 })

// Выбранный компонент
const selectedComp = computed(() => {
  if (!selectedId.value || selectedId.value.startsWith('wire-')) return null
  return components.value.find(c => c.id === selectedId.value)
})

// Методы для работы с компонентами
const addComponent = (type) => {
  const baseComponent = {
    id: Date.now().toString() + Math.random().toString(36).substr(2, 5),
    type,
    x: 100 + Math.random() * 50,
    y: 100 + Math.random() * 50,
    rotation: 0
  }
  
  const specificProps = {
    battery: { voltage: 1.5 },
    led: { color: '#ff0000', isOn: false, brightness: 0 },
    resistor: { resistance: 100 },
    switch: { isOn: false },
    capacitor: { capacitance: 100, charge: 0 },
    transistor_npn: { gain: 100 },
    transistor_pnp: { gain: 100 },
    node: {}
  }
  
  components.value.push({
    ...baseComponent,
    ...specificProps[type]
  })
  
  selectedId.value = baseComponent.id
  stopBlink()
}

const deleteSelected = () => {
  stopBlink()
  if (!selectedId.value) return
  
  if (selectedId.value.startsWith('wire-')) {
    const index = parseInt(selectedId.value.replace('wire-', ''))
    if (!isNaN(index) && index >= 0 && index < wires.value.length) {
      wires.value.splice(index, 1)
    }
    selectedId.value = null
    checkCircuit()
    return
  }
  
  const compId = selectedId.value
  components.value = components.value.filter(c => c.id !== compId)
  wires.value = wires.value.filter(w => w.from.id !== compId && w.to.id !== compId)
  selectedId.value = null
  checkCircuit()
}

const createWire = (fromId, fromPort, toId, toPort) => {
  const fromComp = components.value.find(c => c.id === fromId)
  const toComp = components.value.find(c => c.id === toId)
  if (!fromComp || !toComp) return null
  const fromPortObj = getPorts(fromComp).find(p => p.id === fromPort)
  const toPortObj = getPorts(toComp).find(p => p.id === toPort)
  if (!fromPortObj || !toPortObj) return null
  return {
    from: { id: fromId, port: fromPort },
    to: { id: toId, port: toPort },
    start: getAbsolutePortPosition(fromComp, fromPortObj),
    end: getAbsolutePortPosition(toComp, toPortObj),
    startDir: getPortExitDir(fromComp, fromPortObj),
    endDir: getPortExitDir(toComp, toPortObj),
    offset: (Math.random() - 0.5) * 12
  }
}

const loadScheme = (schemeName) => {
  stopBlink()
  components.value = []
  wires.value = []
  selectedId.value = null
  panOffset.value = { x: 0, y: 0 }

  if (schemeName === 'blinking-led') {
    // Компоненты мультивибратора — компактные координаты
    const bat = { id: 'bat', type: 'battery', x: 150, y: 40, rotation: 0, voltage: 9 }
    const r1 = { id: 'r1', type: 'resistor', x: 40, y: 100, rotation: 90, resistance: 47000 }
    const r2 = { id: 'r2', type: 'resistor', x: 260, y: 100, rotation: 90, resistance: 47000 }
    const q1 = { id: 'q1', type: 'transistor_npn', x: 100, y: 180, rotation: 0, gain: 100 }
    const q2 = { id: 'q2', type: 'transistor_npn', x: 200, y: 180, rotation: 0, gain: 100 }
    const led1 = { id: 'led1', type: 'led', x: 100, y: 280, rotation: 0, color: '#ff0000', isOn: false, brightness: 0 }
    const led2 = { id: 'led2', type: 'led', x: 200, y: 280, rotation: 0, color: '#ff0000', isOn: false, brightness: 0 }
    const c1 = { id: 'c1', type: 'capacitor', x: 170, y: 140, rotation: 0, capacitance: 10, charge: 0 }
    const c2 = { id: 'c2', type: 'capacitor', x: 130, y: 220, rotation: 0, capacitance: 10, charge: 0 }

    components.value = [bat, r1, r2, q1, q2, led1, led2, c1, c2]

    nextTick(() => {
      wires.value = [
        createWire('bat', 'positive', 'r1', 'a'),
        createWire('bat', 'positive', 'r2', 'a'),
        createWire('bat', 'positive', 'led1', 'anode'),
        createWire('bat', 'positive', 'led2', 'anode'),
        createWire('r1', 'b', 'q1', 'base'),
        createWire('r2', 'b', 'q2', 'base'),
        createWire('led1', 'cathode', 'q1', 'collector'),
        createWire('led2', 'cathode', 'q2', 'collector'),
        createWire('c1', 'a', 'q1', 'collector'),
        createWire('c1', 'b', 'q2', 'base'),
        createWire('c2', 'a', 'q2', 'collector'),
        createWire('c2', 'b', 'q1', 'base'),
        createWire('q1', 'emitter', 'bat', 'negative'),
        createWire('q2', 'emitter', 'bat', 'negative'),
      ].filter(Boolean)

      // Авто-центрирование схемы в экране
      const xs = components.value.map(c => c.x)
      const ys = components.value.map(c => c.y)
      const minX = Math.min(...xs)
      const maxX = Math.max(...xs) + 70
      const minY = Math.min(...ys)
      const maxY = Math.max(...ys) + 50
      const centerX = (minX + maxX) / 2
      const centerY = (minY + maxY) / 2

      const container = workspaceRef.value?.parentElement
      const viewW = container?.clientWidth || window.innerWidth
      const viewH = container?.clientHeight || window.innerHeight

      panOffset.value = {
        x: Math.round(viewW / 2 - centerX),
        y: Math.round(viewH / 2 - centerY)
      }
      clampPan()

      checkCircuit()
      startBlink()
    })
  }
}

const startBlink = () => {
  stopBlink()
  blinkMode.value = true
  blinkLeds.value = ['led1', 'led2']

  // Параметры мультивибратора
  const VCC = 9
  const RB = 47000      // сопротивление базы (Ом)
  const C = 10e-6       // ёмкость (Ф)
  const TAU = RB * C    // постоянная времени RC (сек)
  const VBE_ON = 0.65   // порог включения транзистора
  const VBE_OFF = 0.55  // порог выключения
  const VCE_SAT = 0.1   // напряжение насыщения коллектор-эмиттер

  // Начальное состояние: один из транзисторов случайно открывается первым
  let q1On = Math.random() > 0.5
  let q2On = false
  let cap1V = 0   // напряжение на C1 = Vc1 - Vb2
  let cap2V = 0   // напряжение на C2 = Vc2 - Vb1

  const dt = 0.005 // шаг симуляции, сек (5 мс)

  blinkTimer = setInterval(() => {
    const led1 = components.value.find(c => c.id === 'led1')
    const led2 = components.value.find(c => c.id === 'led2')
    const cap1Comp = components.value.find(c => c.id === 'c1')
    const cap2Comp = components.value.find(c => c.id === 'c2')

    // Определяем Vc из состояния транзисторов
    let Vc1 = q1On ? VCE_SAT : VCC
    let Vc2 = q2On ? VCE_SAT : VCC

    // Вычисляем Vb из сохранённых зарядов конденсаторов
    let Vb1 = Vc2 - cap2V
    let Vb2 = Vc1 - cap1V

    // RC-заряд баз через резисторы от Vcc
    // dVb/dt = (VCC - Vb) / TAU
    Vb1 += (VCC - Vb1) * (dt / TAU)
    Vb2 += (VCC - Vb2) * (dt / TAU)

    // Обновляем напряжения на конденсаторах (плавное изменение)
    cap1V = Vc1 - Vb2
    cap2V = Vc2 - Vb1

    // Проверяем переключение транзисторов
    let switched = false

    if (!q1On && Vb1 > VBE_ON) {
      q1On = true
      switched = true
    } else if (q1On && Vb1 < VBE_OFF) {
      q1On = false
      switched = true
    }

    if (!q2On && Vb2 > VBE_ON) {
      q2On = true
      switched = true
    } else if (q2On && Vb2 < VBE_OFF) {
      q2On = false
      switched = true
    }

    // При переключении транзистора напряжение коллектора скачком меняется.
    // Конденсатор сохраняет заряд, поэтому база другого транзистора
    // скачком меняется на ту же величину (ключевой эффект мультивибратора).
    if (switched) {
      let newVc1 = q1On ? VCE_SAT : VCC
      let newVc2 = q2On ? VCE_SAT : VCC
      let dVc1 = newVc1 - Vc1
      let dVc2 = newVc2 - Vc2

      Vb2 += dVc1
      Vb1 += dVc2

      cap1V = newVc1 - Vb2
      cap2V = newVc2 - Vb1
    }

    // Обновляем LED
    if (led1) { led1.isOn = q1On; led1.brightness = q1On ? 1 : 0 }
    if (led2) { led2.isOn = q2On; led2.brightness = q2On ? 1 : 0 }

    // Обновляем отображение заряда конденсаторов
    if (cap1Comp) {
      cap1Comp.charge = Math.min(100, Math.max(0, Math.abs(cap1V) / VCC * 100))
    }
    if (cap2Comp) {
      cap2Comp.charge = Math.min(100, Math.max(0, Math.abs(cap2V) / VCC * 100))
    }
  }, dt * 1000)
}

const stopBlink = () => {
  if (blinkTimer) {
    clearInterval(blinkTimer)
    blinkTimer = null
  }
  blinkMode.value = false
  blinkLeds.value = []
}

const selectComponent = (comp) => {
  selectedId.value = comp.id
}

const handleContainerMouseDown = (event) => {
  if (event.button === 1) {
    event.preventDefault()
    startPan(event)
  }
}

const startPan = (event) => {
  event.preventDefault()
  isPanning.value = true
  panStart.value = { x: event.clientX, y: event.clientY }
  panStartOffset.value = { ...panOffset.value }
  document.addEventListener('mousemove', handlePanMove)
  document.addEventListener('mouseup', stopPan)
}

const WS_SIZE = 5000

const clampPan = () => {
  const container = workspaceRef.value?.parentElement
  const viewW = container?.clientWidth || window.innerWidth
  const viewH = container?.clientHeight || window.innerHeight
  panOffset.value = {
    x: Math.min(0, Math.max(viewW - WS_SIZE, panOffset.value.x)),
    y: Math.min(0, Math.max(viewH - WS_SIZE, panOffset.value.y))
  }
}

const handlePanMove = (event) => {
  if (!isPanning.value) return
  const dx = event.clientX - panStart.value.x
  const dy = event.clientY - panStart.value.y
  panOffset.value = {
    x: panStartOffset.value.x + dx,
    y: panStartOffset.value.y + dy
  }
  clampPan()
}

const stopPan = () => {
  isPanning.value = false
  document.removeEventListener('mousemove', handlePanMove)
  document.removeEventListener('mouseup', stopPan)
}

const clearSelection = () => {
  selectedId.value = null
}

const rotateSelected = () => {
  if (!selectedId.value || selectedId.value.startsWith('wire-')) return
  const comp = components.value.find(c => c.id === selectedId.value)
  if (comp) {
    comp.rotation = ((comp.rotation || 0) + 90) % 360
    nextTick(() => updateWirePositions())
  }
}

const updateParam = (comp, key, value) => {
  comp[key] = value
  checkCircuit()
}

const getLabel = (comp) => {
  const labels = {
    battery: 'Батарея',
    led: 'Светодиод',
    resistor: 'Резистор',
    switch: 'Переключатель',
    capacitor: 'Конденсатор',
    transistor_npn: 'NPN транзистор',
    transistor_pnp: 'PNP транзистор',
    node: 'Узел'
  }
  return labels[comp.type] || comp.type
}

// Провода
const getPorts = (comp) => {
  const portDefinitions = {
    battery: [
      { id: 'positive', type: 'output', x: 0.5, y: 0 },
      { id: 'negative', type: 'input', x: 0.5, y: 1 }
    ],
    led: [
      { id: 'anode', type: 'input', x: 0, y: 0.5 },
      { id: 'cathode', type: 'output', x: 1, y: 0.5 }
    ],
    resistor: [
      { id: 'a', type: 'bidirectional', x: 0, y: 0.5 },
      { id: 'b', type: 'bidirectional', x: 1, y: 0.5 }
    ],
    capacitor: [
      { id: 'a', type: 'bidirectional', x: 0, y: 0.5 },
      { id: 'b', type: 'bidirectional', x: 1, y: 0.5 }
    ],
    transistor_npn: [
      { id: 'collector', type: 'input', x: 0.5, y: 0 },
      { id: 'base', type: 'input', x: 1, y: 0.5 },
      { id: 'emitter', type: 'output', x: 0.5, y: 1 }
    ],
    transistor_pnp: [
      { id: 'collector', type: 'input', x: 0.5, y: 0 },
      { id: 'base', type: 'input', x: 1, y: 0.5 },
      { id: 'emitter', type: 'output', x: 0.5, y: 1 }
    ],
    node: [
      { id: 'center', type: 'bidirectional', x: 0.5, y: 0.5 }
    ],
    switch: [
      { id: 'in', type: 'input', x: 0, y: 0.5 },
      { id: 'out', type: 'output', x: 1, y: 0.5 }
    ]
  }
  return portDefinitions[comp.type] || []
}

const getPortStyle = (comp, port) => {
  const size = 12
  const width = 70
  const height = 50
  const x = port.x * width - size/2
  const y = port.y * height - size/2
  
  const centerX = width / 2
  const centerY = height / 2
  const rad = ((comp.rotation || 0) * Math.PI) / 180
  
  const rotatedX = centerX + (x + size/2 - centerX) * Math.cos(rad) - (y + size/2 - centerY) * Math.sin(rad)
  const rotatedY = centerY + (x + size/2 - centerX) * Math.sin(rad) + (y + size/2 - centerY) * Math.cos(rad)
  
  return {
    left: `${rotatedX - size/2}px`,
    top: `${rotatedY - size/2}px`
  }
}

const isValidConnectionTarget = (comp, port) => {
  if (!tempWire.value) return false
  if (tempWire.value.from.id === comp.id) return false
  
  const isDuplicate = wires.value.some(w => 
    (w.from.id === tempWire.value.from.id && w.from.port === tempWire.value.from.port &&
     w.to.id === comp.id && w.to.port === port.id) ||
    (w.from.id === comp.id && w.from.port === port.id &&
     w.to.id === tempWire.value.from.id && w.to.port === tempWire.value.from.port)
  )
  
  return !isDuplicate
}

const startConnection = (event, comp, port) => {
  if (event.button !== 0) return
  event.stopPropagation()
  selectedId.value = comp.id
  
  if (!workspaceRef.value) return
  
  const rect = workspaceRef.value.getBoundingClientRect()
  const portPos = getAbsolutePortPosition(comp, port)
  
  tempWire.value = {
    from: { id: comp.id, port: port.id },
    start: { x: portPos.x, y: portPos.y },
    startDir: getPortExitDir(comp, port),
    end: { x: event.clientX - rect.left, y: event.clientY - rect.top }
  }
  
  document.addEventListener('mousemove', handleConnectionMove)
  document.addEventListener('mouseup', handleConnectionEnd)
}

const handleConnectionMove = (event) => {
  if (!tempWire.value || !workspaceRef.value) return
  const rect = workspaceRef.value.getBoundingClientRect()
  tempWire.value.end = {
    x: event.clientX - rect.left,
    y: event.clientY - rect.top
  }
}

const handleConnectionEnd = () => {
  if (!tempWire.value) {
    cleanupConnectionListeners()
    return
  }
  tempWire.value = null
  cleanupConnectionListeners()
}

const finishConnection = (event, comp, port) => {
  cleanupConnectionListeners()
  if (!tempWire.value) return
  
  if (tempWire.value.from.id === comp.id) {
    tempWire.value = null
    return
  }
  
  const isDuplicate = wires.value.some(w => 
    (w.from.id === tempWire.value.from.id && w.from.port === tempWire.value.from.port &&
     w.to.id === comp.id && w.to.port === port.id) ||
    (w.from.id === comp.id && w.from.port === port.id &&
     w.to.id === tempWire.value.from.id && w.to.port === tempWire.value.from.port)
  )
  
  if (isDuplicate) {
    tempWire.value = null
    return
  }
  
  const fromComp = components.value.find(c => c.id === tempWire.value.from.id)
  const fromPort = fromComp ? getPorts(fromComp).find(p => p.id === tempWire.value.from.port) : null
  const toComp = components.value.find(c => c.id === comp.id)
  const toPort = toComp ? getPorts(toComp).find(p => p.id === port.id) : null
  
  const newWire = {
    from: tempWire.value.from,
    to: { id: comp.id, port: port.id },
    start: tempWire.value.start,
    end: getAbsolutePortPosition(comp, port),
    startDir: tempWire.value.startDir || (fromComp && fromPort ? getPortExitDir(fromComp, fromPort) : { x: 0, y: 0 }),
    endDir: toComp && toPort ? getPortExitDir(toComp, toPort) : { x: 0, y: 0 },
    offset: (Math.random() - 0.5) * 12
  }
  
  wires.value.push(newWire)
  tempWire.value = null
  stopBlink()
  checkCircuit()
}

const cleanupConnectionListeners = () => {
  document.removeEventListener('mousemove', handleConnectionMove)
  document.removeEventListener('mouseup', handleConnectionEnd)
}

const getAbsolutePortPosition = (comp, port) => {
  const width = 70
  const height = 50
  const x = comp.x + port.x * width
  const y = comp.y + port.y * height
  
  const centerX = comp.x + width / 2
  const centerY = comp.y + height / 2
  const rad = ((comp.rotation || 0) * Math.PI) / 180
  
  const rotatedX = centerX + (x - centerX) * Math.cos(rad) - (y - centerY) * Math.sin(rad)
  const rotatedY = centerY + (x - centerX) * Math.sin(rad) + (y - centerY) * Math.cos(rad)
  
  return { x: rotatedX, y: rotatedY }
}

// Направление выхода провода из порта (чтобы не проходить через компонент)
const getPortExitDir = (comp, port) => {
  const width = 70
  const height = 50
  const px = port.x * width
  const py = port.y * height
  const centerX = width / 2
  const centerY = height / 2
  
  let dx = px - centerX
  let dy = py - centerY
  const len = Math.sqrt(dx * dx + dy * dy)
  if (len > 0) {
    dx /= len
    dy /= len
  }
  
  const rad = ((comp.rotation || 0) * Math.PI) / 180
  return {
    x: dx * Math.cos(rad) - dy * Math.sin(rad),
    y: dx * Math.sin(rad) + dy * Math.cos(rad)
  }
}

const getWirePath = (wire) => {
  if (!wire || !wire.start || !wire.end) return ''
  const { x: x1, y: y1 } = wire.start
  const { x: x2, y: y2 } = wire.end
  const offset = wire.offset || 0
  
  const sdx = wire.startDir?.x || 0
  const sdy = wire.startDir?.y || 0
  const edx = wire.endDir?.x || 0
  const edy = wire.endDir?.y || 0
  
  const sx = x1 + sdx * 25
  const sy = y1 + sdy * 25
  const ex = x2 + edx * 25
  const ey = y2 + edy * 25
  
  const dx = ex - sx
  const dy = ey - sy
  
  if (Math.abs(dx) < 4 || Math.abs(dy) < 4) {
    return `M${x1},${y1} L${sx},${sy} L${ex},${ey} L${x2},${y2}`
  }
  
  if (Math.abs(dx) > Math.abs(dy)) {
    const midY = sy + dy * 0.3 + offset
    return `M${x1},${y1} L${sx},${sy} L${sx},${midY} L${ex},${midY} L${ex},${ey} L${x2},${y2}`
  } else {
    const midX = sx + dx * 0.3 + offset
    return `M${x1},${y1} L${sx},${sy} L${midX},${sy} L${midX},${ey} L${ex},${ey} L${x2},${y2}`
  }
}

const getTempWirePath = () => {
  if (!tempWire.value) return ''
  return getWirePath(tempWire.value)
}

const isPortConnected = (componentId, portId) => {
  return wires.value.some(w => 
    (w.from.id === componentId && w.from.port === portId) ||
    (w.to.id === componentId && w.to.port === portId)
  )
}

const selectWire = (wire, index) => {
  selectedId.value = 'wire-' + index
}

const junctions = computed(() => {
  const count = new Map()
  for (const w of wires.value) {
    const k1 = w.from.id + ':' + w.from.port
    const k2 = w.to.id + ':' + w.to.port
    count.set(k1, (count.get(k1) || 0) + 1)
    count.set(k2, (count.get(k2) || 0) + 1)
  }
  
  const result = []
  for (const [key, c] of count) {
    if (c > 1) {
      const [compId, portId] = key.split(':')
      const comp = components.value.find(x => x.id === compId)
      if (comp) {
        const port = getPorts(comp).find(p => p.id === portId)
        if (port) result.push(getAbsolutePortPosition(comp, port))
      }
    }
  }
  return result
})

// Drag
const startDrag = (comp, event) => {
  if (event.button !== 0) return
  if (!workspaceRef.value) return
  
  isDragging.value = true
  dragComp.value = comp
  selectedId.value = comp.id
  
  const rect = workspaceRef.value.getBoundingClientRect()
  dragOffset.value = {
    x: event.clientX - rect.left - comp.x,
    y: event.clientY - rect.top - comp.y
  }
  
  event.preventDefault()
  event.stopPropagation()
  
  document.addEventListener('mousemove', handleDrag)
  document.addEventListener('mouseup', stopDrag)
}

const handleDrag = (event) => {
  if (!isDragging.value || !dragComp.value) return
  
  const rect = workspaceRef.value.getBoundingClientRect()
  const newX = event.clientX - rect.left - dragOffset.value.x
  const newY = event.clientY - rect.top - dragOffset.value.y
  
  const index = components.value.findIndex(c => c.id === dragComp.value.id)
  if (index !== -1) {
    components.value[index].x = Math.max(0, newX)
    components.value[index].y = Math.max(0, newY)
  }
  
  updateWirePositions()
}

const stopDrag = () => {
  isDragging.value = false
  dragComp.value = null
  document.removeEventListener('mousemove', handleDrag)
  document.removeEventListener('mouseup', stopDrag)
  checkCircuit()
}

const getComponentStyle = (comp) => {
  return {
    left: `${comp.x}px`,
    top: `${comp.y}px`,
    transform: `rotate(${comp.rotation || 0}deg)`,
    '--component-color': comp.color || '#ff0000',
    cursor: isDragging.value && dragComp.value?.id === comp.id ? 'grabbing' : 'grab',
    position: 'absolute',
    userSelect: 'none'
  }
}

const updateWirePositions = () => {
  wires.value.forEach(wire => {
    const fromComp = components.value.find(c => c.id === wire.from.id)
    const toComp = components.value.find(c => c.id === wire.to.id)
    
    if (fromComp) {
      const fromPort = getPorts(fromComp).find(p => p.id === wire.from.port)
      if (fromPort) {
        wire.start = getAbsolutePortPosition(fromComp, fromPort)
        wire.startDir = getPortExitDir(fromComp, fromPort)
      }
    }
    
    if (toComp) {
      const toPort = getPorts(toComp).find(p => p.id === wire.to.port)
      if (toPort) {
        wire.end = getAbsolutePortPosition(toComp, toPort)
        wire.endDir = getPortExitDir(toComp, toPort)
      }
    }
  })
}

// ====== ЛОГИКА ЦЕПИ ======

const buildPortGraph = () => {
  const adj = new Map()
  
  const addEdge = (u, v) => {
    if (!adj.has(u)) adj.set(u, [])
    if (!adj.has(v)) adj.set(v, [])
    adj.get(u).push(v)
    adj.get(v).push(u)
  }
  
  for (const comp of components.value) {
    if (comp.type === 'resistor' || comp.type === 'capacitor') {
      addEdge(comp.id + ':a', comp.id + ':b')
    } else if (comp.type === 'switch') {
      if (comp.isOn) addEdge(comp.id + ':in', comp.id + ':out')
    } else if (comp.type === 'led') {
      addEdge(comp.id + ':anode', comp.id + ':cathode')
    }
  }
  
  for (const wire of wires.value) {
    addEdge(wire.from.id + ':' + wire.from.port, wire.to.id + ':' + wire.to.port)
  }
  
  return adj
}

const hasPathInGraph = (start, end, adj, visited = new Set(), forbidden = null) => {
  if (start === end) return true
  if (visited.has(start)) return false
  if (forbidden && forbidden.has(start)) return false
  visited.add(start)
  
  const neighbors = adj.get(start) || []
  for (const n of neighbors) {
    if (forbidden && forbidden.has(n)) continue
    if (hasPathInGraph(n, end, adj, visited, forbidden)) return true
  }
  return false
}

// Узлы, находящиеся "под напряжением": от батареи или от заряженных конденсаторов
const getPoweredNodes = (adj) => {
  const powered = new Set()
  const battery = components.value.find(c => c.type === 'battery')
  
  const bfs = (startNodes) => {
    const queue = [...startNodes]
    const visited = new Set()
    while (queue.length) {
      const node = queue.shift()
      if (visited.has(node)) continue
      visited.add(node)
      powered.add(node)
      for (const n of adj.get(node) || []) {
        if (!visited.has(n)) queue.push(n)
      }
    }
  }
  
  // От батареи
  if (battery) {
    bfs([battery.id + ':positive', battery.id + ':negative'])
  }
  
  // От заряженных конденсаторов (они хранят энергию и могут питать цепь)
  for (const comp of components.value) {
    if (comp.type === 'capacitor' && (comp.charge || 0) > 0) {
      bfs([comp.id + ':a'])
    }
  }
  
  return powered
}

const checkCircuit = () => {
  // В динамическом режиме мультивибратора статический анализ отключён
  if (blinkMode.value) {
    isCircuitClosed.value = true
    activeComponentIds.value = components.value.map(c => c.id)
    updateComponentsState()
    return
  }

  const battery = components.value.find(c => c.type === 'battery')
  const led = components.value.find(c => c.type === 'led')
  
  if (!battery || !led) {
    isCircuitClosed.value = false
    activeComponentIds.value = []
    updateComponentsState()
    return
  }
  
  // Граф без транзисторов (они изначально закрыты)
  let adj = buildPortGraph()
  const batPos = battery.id + ':positive'
  const batNeg = battery.id + ':negative'
  
  // Итеративно открываем транзисторы, если их base подключена к цепи
  const transistors = components.value.filter(c => c.type === 'transistor_npn' || c.type === 'transistor_pnp')
  let changed = true
  let iterations = 0
  while (changed && iterations < 10) {
    changed = false
    iterations++
    
    const powered = getPoweredNodes(adj)
    
    for (const t of transistors) {
      const base = t.id + ':base'
      const c = t.id + ':collector'
      const e = t.id + ':emitter'
      
      // Base активна, если находится под напряжением (от батареи или заряженного конденсатора)
      let baseActive = false
      if (t.type === 'transistor_npn') {
        baseActive = powered.has(base)
      } else if (t.type === 'transistor_pnp') {
        baseActive = hasPathInGraph(base, batNeg, adj, new Set())
      }
      
      if (baseActive) {
        const neighborsC = adj.get(c) || []
        const neighborsE = adj.get(e) || []
        
        if (!neighborsC.includes(e)) {
          neighborsC.push(e)
          adj.set(c, neighborsC)
          changed = true
        }
        if (!neighborsE.includes(c)) {
          neighborsE.push(c)
          adj.set(e, neighborsE)
          changed = true
        }
      }
    }
  }
  
  const closed = hasPathInGraph(batPos, batNeg, adj)
  
    activeComponentIds.value = []
  
  if (closed) {
    components.value.forEach(comp => {
      const ports = getPorts(comp)
      if (ports.length < 2) return
      
      if (comp.type === 'transistor_npn' || comp.type === 'transistor_pnp') {
        const c = comp.id + ':collector'
        const e = comp.id + ':emitter'
        
        const cToPos = hasPathInGraph(c, batPos, adj, new Set(), new Set([e]))
        const cToNeg = hasPathInGraph(c, batNeg, adj, new Set(), new Set([e]))
        const eToPos = hasPathInGraph(e, batPos, adj, new Set(), new Set([c]))
        const eToNeg = hasPathInGraph(e, batNeg, adj, new Set(), new Set([c]))
        
        if ((cToPos && eToNeg) || (cToNeg && eToPos)) {
          activeComponentIds.value.push(comp.id)
        }
      } else if (comp.type === 'node') {
        const center = comp.id + ':center'
        const toPos = hasPathInGraph(center, batPos, adj, new Set())
        const toNeg = hasPathInGraph(center, batNeg, adj, new Set())
        if (toPos && toNeg) activeComponentIds.value.push(comp.id)
      } else {
        const p1 = comp.id + ':' + ports[0].id
        const p2 = comp.id + ':' + ports[1].id
        
        const p1ToPos = hasPathInGraph(p1, batPos, adj, new Set(), new Set([p2]))
        const p1ToNeg = hasPathInGraph(p1, batNeg, adj, new Set(), new Set([p2]))
        const p2ToPos = hasPathInGraph(p2, batPos, adj, new Set(), new Set([p1]))
        const p2ToNeg = hasPathInGraph(p2, batNeg, adj, new Set(), new Set([p1]))
        
        if ((p1ToPos && p2ToNeg) || (p1ToNeg && p2ToPos)) {
          activeComponentIds.value.push(comp.id)
        }
      }
    })
  }
  
  isCircuitClosed.value = closed
  updateComponentsState()
}

const updateComponentsState = () => {
  components.value.forEach(comp => {
    if (comp.type === 'led') {
      if (blinkMode.value && blinkLeds.value.includes(comp.id)) {
        // состояние управляется таймером мигания
      } else {
        comp.isOn = activeComponentIds.value.includes(comp.id)
        comp.brightness = comp.isOn ? 1 : 0
      }
    }
  })
}

const toggleSwitch = (comp) => {
  if (comp.type !== 'switch') return
  comp.isOn = !comp.isOn
  checkCircuit()
}

const isPowered = (comp) => {
  if (!isCircuitClosed.value) return false
  if (comp.type === 'battery') return true
  return activeComponentIds.value.includes(comp.id)
}

// Расчёт параметров цепи
const circuitInfo = computed(() => {
  const battery = components.value.find(c => c.type === 'battery')
  const voltage = battery ? battery.voltage : 0
  
  let current = 0
  let power = 0
  let totalResistance = 0
  
  if (isCircuitClosed.value && battery) {
    const active = components.value.filter(c => activeComponentIds.value.includes(c.id))
    
    totalResistance = 0.5
    for (const c of active) {
      if (c.type === 'resistor') totalResistance += c.resistance
      else if (c.type === 'led') totalResistance += 10
      else if (c.type === 'switch') totalResistance += 0.1
      else if (c.type === 'capacitor') totalResistance += 0.01
      else if (c.type === 'transistor_npn' || c.type === 'transistor_pnp') totalResistance += 0.01
      else if (c.type === 'node') totalResistance += 0
    }
    
    current = voltage / totalResistance
    power = voltage * current
  }
  
  return {
    isClosed: isCircuitClosed.value,
    voltage,
    current,
    power,
    totalResistance,
    components: components.value.map(c => {
      const inCircuit = activeComponentIds.value.includes(c.id)
      let r = 0
      if (c.type === 'resistor') r = c.resistance
      else if (c.type === 'led') r = 10
      else if (c.type === 'switch') r = 0.1
      else if (c.type === 'capacitor') r = 0.01
      else if (c.type === 'transistor_npn' || c.type === 'transistor_pnp') r = 0.01
      else if (c.type === 'node') r = 0
      else if (c.type === 'battery') r = 0.5
      
      return {
        id: c.id,
        type: c.type,
        label: getLabel(c),
        r,
        u: inCircuit ? current * r : 0,
        p: inCircuit ? current * current * r : 0,
        i: inCircuit ? current : 0,
        voltage: c.voltage,
        resistance: c.resistance,
        color: c.color,
        isOn: c.isOn,
        capacitance: c.capacitance,
        charge: c.charge || 0
      }
    })
  }
})

// Наблюдатели
watch(wires, () => checkCircuit(), { deep: true })
watch(components, () => updateWirePositions(), { deep: true })

let chargeInterval = null

onMounted(() => {
  const container = document.querySelector('.simulator-container')
  if (container) container.focus()
  updateWirePositions()
  
  chargeInterval = setInterval(() => {
    let changed = false
    components.value.forEach(c => {
      if (c.type !== 'capacitor') return
      const inCircuit = activeComponentIds.value.includes(c.id)
      const target = isCircuitClosed.value && inCircuit ? 100 : 0
      const step = isCircuitClosed.value && inCircuit ? Math.max(1, 200 / (c.capacitance || 100)) : 4
      if ((c.charge || 0) < target) {
        c.charge = Math.min(target, (c.charge || 0) + step)
        changed = true
      } else if ((c.charge || 0) > target) {
        c.charge = Math.max(target, (c.charge || 0) - step)
        changed = true
      }
    })
    if (changed) checkCircuit()
  }, 100)
})

onUnmounted(() => {
  if (chargeInterval) clearInterval(chargeInterval)
  stopBlink()
  stopPan()
})
</script>

<style scoped>
.simulator-container {
  position: relative;
  width: 100%;
  height: 100vh;
  overflow: hidden;
  background-color: #f0f0f0;
  outline: none;
}

.toolbar {
  position: absolute;
  top: 10px;
  left: 10px;
  display: flex;
  gap: 8px;
  background: white;
  padding: 10px;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.12);
  z-index: 100;
  flex-wrap: wrap;
}

.toolbar button {
  padding: 8px 14px;
  border: 1px solid #ddd;
  border-radius: 6px;
  background: #fafafa;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 14px;
  transition: all 0.2s ease;
}

.toolbar button:hover:not(:disabled) {
  background: #f0f0f0;
  border-color: #bbb;
  transform: translateY(-1px);
}

.toolbar button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.toolbar-divider {
  width: 1px;
  background: #ddd;
  margin: 0 4px;
}

/* Панель свойств */
.properties-panel {
  position: absolute;
  top: 70px;
  right: 10px;
  width: 220px;
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0,0,0,0.1);
  padding: 14px;
  z-index: 100;
}

.properties-panel h3 {
  margin: 0 0 12px 0;
  font-size: 14px;
  color: #333;
  border-bottom: 1px solid #eee;
  padding-bottom: 8px;
}

.prop-row {
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-bottom: 12px;
}

.prop-row label {
  font-size: 12px;
  color: #666;
  font-weight: 500;
}

.prop-row input[type="number"],
.prop-row input[type="color"] {
  padding: 6px 8px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 13px;
  width: 100%;
  box-sizing: border-box;
}

.prop-row input[type="color"] {
  height: 32px;
  padding: 2px;
  cursor: pointer;
}

.toggle-btn {
  padding: 6px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  background: #f5f5f5;
  cursor: pointer;
  font-size: 13px;
  transition: all 0.2s;
}

.toggle-btn.on {
  background: #4CAF50;
  color: white;
  border-color: #4CAF50;
}

.workspace {
  position: absolute;
  top: 0;
  left: 0;
  width: 5000px;
  height: 5000px;
  background-image: 
    linear-gradient(#d0d0d0 1px, transparent 1px),
    linear-gradient(90deg, #d0d0d0 1px, transparent 1px);
  background-size: 20px 20px;
  cursor: grab;
}

.workspace.panning {
  cursor: grabbing;
}

.circuit-component {
  position: absolute;
  width: 70px;
  height: 50px;
  border: 2px solid #888;
  border-radius: 6px;
  background: #ffffff;
  cursor: grab;
  user-select: none;
  transition: box-shadow 0.15s ease, border-color 0.15s ease;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 2;
}

.circuit-component.selected {
  border-color: #2196F3;
  box-shadow: 0 0 0 3px rgba(33, 150, 243, 0.25);
  z-index: 100;
}

.circuit-component.powered {
  border-color: #f4b400;
  box-shadow: 0 0 8px rgba(244, 180, 0, 0.4);
}

.circuit-component.dragging {
  z-index: 1000;
  opacity: 0.95;
  cursor: grabbing;
}

.circuit-component.type-led {
  background: #2a2a2a;
}

.circuit-component.type-battery {
  background: #fffde7;
}

.circuit-component.type-resistor {
  background: #e3f2fd;
}

.circuit-component.type-switch {
  background: #ffffff;
}

.circuit-component.type-capacitor {
  background: #f3e5f5;
}

.circuit-component.type-transistor_npn,
.circuit-component.type-transistor_pnp {
  background: #e8f5e9;
}

.circuit-component.type-node {
  width: 16px;
  height: 16px;
  background: transparent;
  border: none;
  border-radius: 50%;
}

.prop-row.read-only {
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
}

.charge-value {
  font-weight: 600;
  color: #4CAF50;
  font-size: 13px;
}

.charge-badge {
  display: inline-block;
  padding: 1px 5px;
  border-radius: 10px;
  background: #E8F5E9;
  color: #2E7D32;
  font-size: 10px;
  font-weight: 600;
  margin-left: 4px;
}

.component-symbol {
  pointer-events: none;
}

.component-label {
  font-size: 9px;
  font-weight: 600;
  color: #555;
  pointer-events: none;
  margin-top: 1px;
  letter-spacing: 0.3px;
}

.circuit-component.type-led .component-label {
  color: #ccc;
}

.port {
  position: absolute;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: #2196F3;
  cursor: crosshair;
  z-index: 3;
  transition: all 0.15s ease;
  border: 2px solid white;
  box-shadow: 0 1px 3px rgba(0,0,0,0.2);
}

.port:hover {
  transform: scale(1.4);
  background: #1976D2;
  box-shadow: 0 0 6px rgba(25, 118, 210, 0.5);
}

.port.connected {
  background: #4CAF50;
}

.port.input {
  background: #4CAF50;
}

.port.output {
  background: #F44336;
}

.port.connection-target {
  background: #FF9800;
  box-shadow: 0 0 8px rgba(255, 152, 0, 0.9);
  transform: scale(1.5);
}

.wires {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  z-index: 1;
}

.wires path {
  stroke: #555;
  stroke-width: 2.5;
  fill: none;
  pointer-events: stroke;
  cursor: pointer;
  transition: stroke 0.2s ease;
  stroke-linecap: round;
  stroke-linejoin: round;
}

.wires path:hover {
  stroke: #222;
  stroke-width: 3.5;
}

.wires path.selected {
  stroke: #2196F3;
  stroke-width: 3.5;
}

.wires path.active {
  stroke: #4CAF50;
  stroke-width: 3;
  stroke-dasharray: 10 5;
  animation: electric-flow 0.5s linear infinite;
}

.wires path.temp {
  stroke: #999;
  stroke-dasharray: 5;
  stroke-width: 2;
  pointer-events: none;
}

.wires circle.junction {
  pointer-events: none;
  stroke: #f0f0f0;
  stroke-width: 2;
}

@keyframes electric-flow {
  from { stroke-dashoffset: 15; }
  to { stroke-dashoffset: 0; }
}

/* LED свечение */
.led-glow-overlay {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 30px;
  height: 30px;
  border-radius: 50%;
  background: var(--led-color, #ff0000);
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.3s ease;
  filter: blur(8px);
  z-index: 1;
}

.led-glow-overlay.on {
  opacity: 0.6;
}

.circuit-component.type-led.powered .led-glow-overlay.on {
  opacity: 0.8;
  box-shadow: 0 0 20px var(--led-color, #ff0000);
}

/* Ручка переключателя */
.switch-thumb-ui {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: #e0e0e0;
  border: 2px solid #555;
  cursor: pointer;
  z-index: 5;
  transition: all 0.25s ease;
}

.switch-thumb-ui.on {
  background: #4CAF50;
  border-color: #388E3C;
  box-shadow: 0 0 6px rgba(76, 175, 80, 0.5);
}

/* Инфо-панель цепи */
.circuit-info {
  position: absolute;
  bottom: 20px;
  right: 10px;
  width: 280px;
  background: white;
  border-radius: 10px;
  box-shadow: 0 2px 10px rgba(0,0,0,0.1);
  z-index: 100;
  overflow: hidden;
}

.circuit-status {
  padding: 12px 16px;
  font-size: 14px;
  color: #666;
  display: flex;
  align-items: center;
  gap: 8px;
  border-bottom: 1px solid #f0f0f0;
  transition: all 0.3s ease;
}

.circuit-status.active {
  background: #E8F5E9;
  color: #1B5E20;
  border-bottom-color: #C8E6C9;
}

.status-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: #bbb;
  transition: all 0.3s ease;
  flex-shrink: 0;
}

.circuit-status.active .status-dot {
  background: #4CAF50;
  box-shadow: 0 0 8px #4CAF50;
}

.circuit-stats {
  padding: 10px 16px;
  background: #fafafa;
  border-bottom: 1px solid #f0f0f0;
}

.stats-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
}

.stat-item {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.stat-label {
  font-size: 11px;
  color: #888;
}

.stat-value {
  font-size: 13px;
  font-weight: 600;
  color: #333;
}

.components-list {
  max-height: 220px;
  overflow-y: auto;
  padding: 8px 0;
}

.list-header {
  padding: 4px 16px 8px;
  font-size: 11px;
  font-weight: 600;
  color: #999;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.comp-stat {
  padding: 6px 16px;
  cursor: pointer;
  transition: background 0.15s;
  border-left: 3px solid transparent;
}

.comp-stat:hover {
  background: #f5f5f5;
}

.comp-stat.active {
  background: #E3F2FD;
  border-left-color: #2196F3;
}

.comp-main {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 13px;
}

.comp-name {
  font-weight: 500;
  color: #333;
}

.comp-params {
  font-size: 12px;
  color: #666;
  display: flex;
  align-items: center;
  gap: 4px;
}

.color-dot {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  border: 1px solid rgba(0,0,0,0.2);
  display: inline-block;
}

.comp-calc {
  font-size: 11px;
  color: #888;
  margin-top: 2px;
  font-family: monospace;
}
</style>
